import { type BlockitEnv, BlockitEnvEnumValue } from '#blockitEnv.js';
import {
  AMPLITUDE_API_KEY,
  DATADOG_RUM_APPLICATION_ID,
  DATADOG_RUM_CLIENT_TOKEN,
  GOOGLE_MAPS_BROWSER_API_KEY,
} from '#constants.js';

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  appId: string;
}

export interface DatadogRumConfig {
  applicationId: string;
  clientToken: string;
  env: string;
}

export interface StripeWebConfig {
  publishableKey: string;
  pricingTableId: string;
}

/** Everything here ships in the browser bundle, so it must never hold a secret. */
export interface WebConfig {
  apiUrl: string;
  firebase: FirebaseWebConfig;
  googleMapsApiKey: string;
  slackAppClientId: string;
  zoomClientId: string;
  amplitudeApiKey: string | null;
  datadogRum: DatadogRumConfig | null;
  stripe: StripeWebConfig;
}

/** Per-developer values a local run may substitute: a worktree's own server port, a personal Slack app. */
export interface WebDevOverrides {
  apiUrl: string | null;
  slackAppClientId: string | null;
}

const STAGING_FIREBASE: FirebaseWebConfig = {
  apiKey: 'AIzaSyBpMcAmye-jZ_VjInZmch_FANa9f7h6zWE',
  authDomain: 'blockit-staging.firebaseapp.com',
  projectId: 'blockit-staging',
  storageBucket: 'blockit-staging.firebasestorage.app',
  appId: '1:301444326645:web:d0ad884f5edf7c968ed1bc',
};

const STRIPE_TEST: StripeWebConfig = {
  publishableKey:
    'pk_test_51PCEGTP3mjiLxBkSBTFmhgoK1LHP4u0faASTDQ3q8wszwDaWQpdqofWYf430eAb2ezsiOtmxyxp2n6x3Tr3hmXFV00AZ1xO2aO',
  pricingTableId: 'prctbl_1UBPIJP3mjiLxBkSHHQQ0nZX',
};

const STAGING_ZOOM_CLIENT_ID = 'QvXgtJaCSYOGdhzM4RDpYw';

export const DEV_WEB_CONFIG: WebConfig = {
  apiUrl: 'http://localhost:8000',
  firebase: STAGING_FIREBASE,
  googleMapsApiKey: GOOGLE_MAPS_BROWSER_API_KEY,
  slackAppClientId: '5419298431573.6291637482614',
  zoomClientId: STAGING_ZOOM_CLIENT_ID,
  amplitudeApiKey: AMPLITUDE_API_KEY,
  datadogRum: null,
  stripe: STRIPE_TEST,
};

export const STAGING_WEB_CONFIG: WebConfig = {
  apiUrl: 'https://blockit-server-staging.onrender.com',
  firebase: STAGING_FIREBASE,
  googleMapsApiKey: GOOGLE_MAPS_BROWSER_API_KEY,
  slackAppClientId: '5419298431573.8746775853426',
  zoomClientId: STAGING_ZOOM_CLIENT_ID,
  amplitudeApiKey: null,
  datadogRum: {
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    env: 'staging',
  },
  stripe: STRIPE_TEST,
};

export const PROD_WEB_CONFIG: WebConfig = {
  apiUrl: 'https://api.blockit.com',
  firebase: {
    apiKey: 'AIzaSyC07N4ae-QqW8gsteQSQ46sRtdGhXrLVic',
    authDomain: 'blockit-prod.firebaseapp.com',
    projectId: 'blockit-prod',
    storageBucket: 'blockit-prod.appspot.com',
    appId: '1:49146863114:web:40144422184da15b9f89f3',
  },
  googleMapsApiKey: GOOGLE_MAPS_BROWSER_API_KEY,
  slackAppClientId: '5419298431573.6306566927639',
  zoomClientId: 'UvWVr5cVRiaQuBsn7FYhLQ',
  amplitudeApiKey: AMPLITUDE_API_KEY,
  datadogRum: {
    applicationId: DATADOG_RUM_APPLICATION_ID,
    clientToken: DATADOG_RUM_CLIENT_TOKEN,
    env: 'production',
  },
  stripe: {
    publishableKey:
      'pk_live_51PCEGTP3mjiLxBkSF0ZwjZ9mPn28c30W0CBLtaD1QPA3R5uV7369s8CvH8Dkkd75TyaBEpQ1OkTLTlRURAydZVF0007674ZNB0',
    pricingTableId: 'prctbl_1SpHV5P3mjiLxBkSSyRIp9ep',
  },
};

export const WEB_CONFIGS: Record<BlockitEnv, WebConfig> = {
  dev: DEV_WEB_CONFIG,
  staging: STAGING_WEB_CONFIG,
  prod: PROD_WEB_CONFIG,
};

export const applyDevOverrides = ({
  blockitEnv,
  config,
  devOverrides,
}: {
  blockitEnv: BlockitEnv;
  config: WebConfig;
  devOverrides: WebDevOverrides;
}): WebConfig => {
  if (blockitEnv !== BlockitEnvEnumValue.DEV) {
    return config;
  }
  return {
    ...config,
    apiUrl: devOverrides.apiUrl ?? config.apiUrl,
    slackAppClientId: devOverrides.slackAppClientId ?? config.slackAppClientId,
  };
};
